import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { User } from 'lucide-react';

const BASE = process.env.REACT_APP_BACKEND_URL;

const auth = () => {
  const t = localStorage.getItem('access_token') || localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function DirectorOfWorksDialog({ open, onOpenChange, project, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [mode, setMode] = useState('user'); // 'user' | 'external'
  const [form, setForm] = useState({
    user_id: '',
    name_snapshot: '',
    qualification_snapshot: '',
    registration_snapshot: '',
  });

  useEffect(() => {
    if (!open || !project) return;
    setForm({
      user_id: project.director_of_works_user_id || '',
      name_snapshot: project.director_of_works_name_snapshot || '',
      qualification_snapshot: project.director_of_works_qualification_snapshot || '',
      registration_snapshot: project.director_of_works_registration_snapshot || '',
    });
    setMode(project.director_of_works_user_id ? 'user' : 'external');
  }, [open, project]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetch(`${BASE}/api/users`, { headers: { ...auth() } })
      .then(r => r.ok ? r.json() : [])
      .then(list => {
        const arr = Array.isArray(list) ? list : list.items || [];
        setUsers(arr.filter(u => u.is_active !== false));
      })
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  }, [open]);

  const handleUserSelect = (uid) => {
    const u = users.find(x => x.id === uid);
    if (u) {
      setForm({
        user_id: uid,
        name_snapshot: `${u.first_name || ''} ${u.last_name || ''}`.trim(),
        qualification_snapshot: u.qualification || form.qualification_snapshot,
        registration_snapshot: form.registration_snapshot,
      });
    } else {
      setForm({ ...form, user_id: uid });
    }
  };

  const handleSave = async () => {
    const body = {
      user_id: mode === 'user' ? (form.user_id || null) : null,
      name_snapshot: form.name_snapshot?.trim() || null,
      qualification_snapshot: form.qualification_snapshot?.trim() || null,
      registration_snapshot: form.registration_snapshot?.trim() || null,
    };
    if (!body.name_snapshot) return toast.error('Nome Direttore Lavori obbligatorio');
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/api/projects/${project.id}/director-of-works`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...auth() },
        body: JSON.stringify(body),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j?.detail?.message || j?.detail || `HTTP ${res.status}`);
      toast.success('Direttore Lavori aggiornato');
      onSaved && onSaved(j);
      onOpenChange(false);
    } catch (e) {
      toast.error(`Errore: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="dl-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5" /> Direttore Lavori
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex gap-2">
            <Button
              type="button"
              variant={mode === 'user' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setMode('user')}
              data-testid="dl-mode-user"
            >
              Utente CRM
            </Button>
            <Button
              type="button"
              variant={mode === 'external' ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setMode('external'); setForm({ ...form, user_id: '' }); }}
              data-testid="dl-mode-external"
            >
              Professionista esterno
            </Button>
          </div>

          {mode === 'user' && (
            <div>
              <Label>Utente CRM *</Label>
              <Select value={form.user_id} onValueChange={handleUserSelect}>
                <SelectTrigger data-testid="dl-user-select"><SelectValue placeholder={loading ? 'Caricamento…' : 'Seleziona utente'} /></SelectTrigger>
                <SelectContent>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      {`${u.first_name || ''} ${u.last_name || ''}`.trim()} · {u.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label>Nome e cognome *</Label>
            <Input
              value={form.name_snapshot}
              onChange={(e) => setForm({ ...form, name_snapshot: e.target.value })}
              placeholder="Cognome Nome"
              data-testid="dl-name"
              disabled={mode === 'user' && !!form.user_id}
            />
          </div>
          <div>
            <Label>Qualifica</Label>
            <Input
              value={form.qualification_snapshot}
              onChange={(e) => setForm({ ...form, qualification_snapshot: e.target.value })}
              placeholder="es. Ing. / Arch. / Geom."
              data-testid="dl-qualification"
            />
          </div>
          <div>
            <Label>Ordine / N. iscrizione</Label>
            <Input
              value={form.registration_snapshot}
              onChange={(e) => setForm({ ...form, registration_snapshot: e.target.value })}
              placeholder="es. Ordine Ingegneri Napoli n. 12345"
              data-testid="dl-registration"
            />
          </div>

          <p className="text-xs text-slate-500">
            Al momento dell'emissione del Report SAL, questi dati vengono <strong>snapshottati</strong>
            e diventano immutabili sul documento emesso.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="dl-save">
            {saving ? 'Salvo…' : 'Salva'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
